import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { register } from "../../redux/actions/authAction";
import { useTranslation } from "react-i18next";
import { getDataAPI } from "../../utils/API";

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  fontSize: 14,
  color: "#111827",
  background: "#f9fafb",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
  fontFamily: "inherit",
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 };
const errorStyle = { fontSize: 12, color: "#ef4444", marginTop: 4, display: "block", minHeight: 18 };

// ─── KYC Step Component ───────────────────────────────────────────────────────
// Nafath OIDC v2 web flow (pre-production provisioned by ELM for AppID 06fl8773):
//   1. GET /api/nafath/session → { sessionToken, nafathUrl }
//   2. Browser goes to nafathUrl (iam.sa) where the user signs in with Nafath
//   3. Nafath returns to /api/nafath/callback (backend exchanges + validates the JWT)
//   4. Backend redirects to /register?kyc=verified&session={sessionToken}
//      (or ?kyc=failed&reason=…)
//   5. On mount we read the identity from GET /api/nafath/status/{sessionToken}
const NAFATH_GREEN = "#0e3725";

const KYC_FAILURE_REASONS = {
  unknown_state: { en: "Your Nafath session expired. Please start again.", ar: "انتهت صلاحية جلسة نفاذ. يرجى البدء من جديد." },
  invalid_token: { en: "Nafath could not confirm your identity. Please try again.", ar: "تعذر على نفاذ تأكيد هويتك. يرجى المحاولة مرة أخرى." },
  server_error:  { en: "We could not complete the Nafath verification. Please try again.", ar: "تعذر إكمال التحقق عبر نفاذ. يرجى المحاولة مرة أخرى." },
};

const maskId = (id = "") => (id.length > 4 ? `${"•".repeat(id.length - 4)}${id.slice(-4)}` : id);

const ShieldIcon = ({ size = 22, color = "#c9a84c" }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const Spinner = ({ color = NAFATH_GREEN }) => (
  <>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" style={{ animation: "nafathSpin 1.2s linear infinite", flexShrink: 0 }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
    <style>{`@keyframes nafathSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </>
);

const Stepper = ({ step, isRtl }) => {
  const steps = isRtl ? ["التحقق من الهوية", "بيانات الحساب"] : ["Verify identity", "Account details"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <React.Fragment key={label}>
            {i > 0 && <div style={{ flex: 1, height: 2, background: step > i ? "#22c55e" : "#e5e7eb", borderRadius: 2 }} />}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700,
                background: done ? "#22c55e" : active ? NAFATH_GREEN : "#e5e7eb", color: done || active ? "#fff" : "#6b7280" }}>
                {done ? <svg width="13" height="13" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg> : n}
              </div>
              <span style={{ fontSize: 13, fontWeight: active ? 700 : 600, color: active ? "#111827" : "#6b7280", whiteSpace: "nowrap" }}>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const NafathKycStep = ({ onVerified, initialKycStatus, initialSessionToken, initialReason }) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const lang = isRtl ? "ar" : "en";

  const failureMessage = (reason) => (KYC_FAILURE_REASONS[reason] || KYC_FAILURE_REASONS.server_error)[lang];

  const [status, setStatus] = useState(() => {
    if (initialKycStatus === "verified" && initialSessionToken) return "loading";
    if (initialKycStatus === "failed") return "error";
    return "idle";
  });
  const [errorMsg, setErrorMsg] = useState(initialKycStatus === "failed" ? failureMessage(initialReason) : "");

  // Returning from Nafath with ?kyc=verified → fetch the verified identity
  useEffect(() => {
    if (initialKycStatus !== "verified" || !initialSessionToken) return;
    getDataAPI(`nafath/status/${initialSessionToken}`)
      .then((res) => {
        if (res.data?.kycStatus === "verified") {
          onVerified({ sessionToken: initialSessionToken, identity: res.data.identity });
        } else {
          setStatus("error");
          setErrorMsg(failureMessage("unknown_state"));
        }
      })
      .catch(() => {
        setStatus("error");
        setErrorMsg(failureMessage("server_error"));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startKyc = async () => {
    setStatus("redirecting");
    setErrorMsg("");
    try {
      const res = await getDataAPI(`nafath/session?locale=${lang}`);
      if (!res.data?.status || !res.data?.nafathUrl) throw new Error("no session");
      window.location.assign(res.data.nafathUrl);
    } catch (err) {
      setStatus("error");
      setErrorMsg(isRtl ? "تعذر الاتصال بنفاذ حالياً. يرجى المحاولة بعد قليل." : "Could not reach Nafath right now. Please try again shortly.");
    }
  };

  const card = { border: "1.5px solid #e5e7eb", borderRadius: 14, padding: "22px 22px 20px", background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" };

  if (status === "loading") {
    return (
      <div style={{ ...card, display: "flex", alignItems: "center", gap: 12 }}>
        <Spinner />
        <span style={{ fontSize: 14, color: "#374151" }}>{isRtl ? "جارٍ استلام بيانات هويتك من نفاذ…" : "Retrieving your verified identity from Nafath…"}</span>
      </div>
    );
  }

  const steps = isRtl
    ? ["اضغط «المتابعة عبر نفاذ» وسيتم تحويلك إلى منصة النفاذ الوطني الموحد.", "سجّل الدخول برقم هويتك ووافق على الطلب في تطبيق نفاذ.", "ستعود تلقائياً إلى هنا لإكمال بيانات حسابك."]
    : ["Tap “Continue with Nafath” to go to the National Single Sign-On (iam.sa).", "Sign in with your National ID and approve the request in the Nafath app.", "You’ll return here automatically to finish your account details."];

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: NAFATH_GREEN, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ShieldIcon />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>{isRtl ? "تحقق من هويتك عبر نفاذ" : "Verify your identity with Nafath"}</div>
          <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 2 }}>{isRtl ? "النفاذ الوطني الموحد — خطوة لمرة واحدة" : "National Single Sign-On — one-time step"}</div>
        </div>
      </div>

      <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, margin: "0 0 14px" }}>
        {isRtl
          ? "تلتزم الخزينة بالتحقق من هوية كل مستثمر وفق متطلبات هيئة السوق المالية. لا نطّلع على كلمة مرورك، ونستلم من نفاذ بيانات هويتك الموثّقة فقط."
          : "Treasury verifies every investor’s identity as required by the Capital Market Authority. We never see your Nafath password, only your verified identity details."}
      </p>

      <ol style={{ listStyle: "none", padding: 0, margin: "0 0 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {steps.map((txt, i) => (
          <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: "#4b5563", lineHeight: 1.6 }}>
            <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#f3f4f6", color: NAFATH_GREEN, fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
            {txt}
          </li>
        ))}
      </ol>

      {status === "error" && (
        <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "#dc2626" }}>
          {errorMsg}
        </div>
      )}

      <button
        type="button"
        onClick={startKyc}
        disabled={status === "redirecting"}
        style={{ width: "100%", padding: "13px 20px", background: `linear-gradient(135deg, ${NAFATH_GREEN}, #1a5c3a)`, color: "#fff", border: "none", borderRadius: 10,
          fontSize: 14.5, fontWeight: 700, cursor: status === "redirecting" ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: status === "redirecting" ? 0.85 : 1 }}
      >
        {status === "redirecting" ? <Spinner color="#fff" /> : <ShieldIcon size={17} color="#fff" />}
        {status === "redirecting"
          ? (isRtl ? "جارٍ التحويل إلى نفاذ…" : "Redirecting to Nafath…")
          : status === "error"
            ? (isRtl ? "إعادة المحاولة عبر نفاذ" : "Try again with Nafath")
            : (isRtl ? "المتابعة عبر نفاذ" : "Continue with Nafath")}
      </button>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 12, fontSize: 11.5, color: "#9ca3af" }}>
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        {isRtl ? "اتصال آمن بالنفاذ الوطني الموحد (iam.sa)" : "Secure connection to the National Single Sign-On (iam.sa)"}
      </div>
    </div>
  );
};

const VerifiedIdentityCard = ({ identity, isRtl }) => {
  const displayName = (isRtl ? identity.arabicName || identity.englishName : identity.englishName || identity.arabicName) || "";
  return (
    <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
      <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2.6" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#166534", fontSize: 13.5 }}>{isRtl ? "تم التحقق من هويتك عبر نفاذ" : "Identity verified via Nafath"}</div>
        <div style={{ color: "#15803d", fontSize: 12.5, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayName && <span style={{ fontWeight: 600 }}>{displayName}</span>}
          {displayName && identity.nationalId && " · "}
          {identity.nationalId && <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{maskId(identity.nationalId)}</span>}
        </div>
      </div>
    </div>
  );
};

// ─── Main Register Component ──────────────────────────────────────────────────
const Register = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const dispatch = useDispatch();
  const history = useHistory();
  const [checkBox, setCheckBox] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState("");
  // Set once Nafath verification succeeds: { sessionToken, identity }.
  // The account form stays locked until then.
  const [kycData, setKycData] = useState(null);
  const { auth } = useSelector((state) => state);

  // Read ?kyc=verified&session=xxx from URL query params (set by backend after Nafath callback)
  const params = new URLSearchParams(window.location.search);
  const initialKycStatus    = params.get("kyc");      // "verified" | "failed" | null
  const initialSessionToken = params.get("session");  // our sessionToken
  const initialReason       = params.get("reason");   // failure reason, if any

  // Clear any stale auth error on mount
  useEffect(() => {
    if (auth.data?.status === 0) {
      dispatch({ type: "AUTH", payload: {} });
    }
    // Clean up URL query params so a page refresh doesn't re-trigger the flow
    if (initialKycStatus) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (auth.data) {
    const response = auth.data;
    if (response.status == 1 && response.action == "register") {
      history.push("/login");
    }
  }

  const validate = (name, value) => {
    const errs = { ...errors };
    if (name === "name") {
      if (!value) errs.name = t("auth.nameRequired");
      else if (!/^[A-Za-z\u0600-\u06FF\s]+$/.test(value)) errs.name = t("auth.nameLettersOnly");
      else delete errs.name;
    }
    if (name === "email") {
      if (!value) errs.email = t("auth.emailRequired");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = t("auth.emailInvalid");
      else delete errs.email;
    }
    if (name === "national_id") {
      if (!value) errs.national_id = t("auth.nationalIdRequired");
      else if (value.length < 3 || value.length > 10) errs.national_id = t("auth.nationalIdLength");
      else delete errs.national_id;
    }
    if (name === "phone_no") {
      if (!value) errs.phone_no = t("auth.phoneRequired");
      else if (value.length !== 8) errs.phone_no = t("auth.phoneLength");
      else delete errs.phone_no;
    }
    if (name === "password") {
      if (!value) errs.password = t("auth.passwordRequired");
      else if (!/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/.test(value))
        errs.password = t("auth.passwordStrength");
      else delete errs.password;
    }
    if (name === "repeat_password") {
      const pwd = document.querySelector('[name="password"]')?.value || "";
      if (!value) errs.repeat_password = t("auth.confirmPasswordRequired");
      else if (value !== pwd) errs.repeat_password = t("auth.passwordMismatch");
      else delete errs.repeat_password;
    }
    setErrors(errs);
  };

  const handleChange = (e) => validate(e.target.name, e.target.value);

  // A Nafath session is single-use and short-lived; if the server rejects it
  // at submit time, send the user back to step 1 to verify again.
  useEffect(() => {
    if (auth.data?.status === 0 && auth.data?.errors?.kyc) setKycData(null);
  }, [auth.data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, national_id, phone_no, password, repeat_password } = e.target.elements;
    dispatch(register({
      name: name.value,
      email: email.value,
      national_id: national_id.value,
      phone_no: phone_no.value,
      password: password.value,
      repeat_password: repeat_password.value,
      kycSessionToken: kycData?.sessionToken,
    }));
  };

  const serverErrors = auth.data?.errors || {};
  const verifiedIdentity = kycData?.identity || {};
  const verifiedName = (isRtl ? verifiedIdentity.arabicName || verifiedIdentity.englishName : verifiedIdentity.englishName || verifiedIdentity.arabicName) || "";

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "#f8f9fa" }}>
      {/* Left Panel */}
      <div style={{ flex: 1, background: "linear-gradient(160deg, #0e3725 0%, #1a5c3a 60%, #c9a84c 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 40px", minHeight: "100vh" }} className="register-left-panel">
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 14, textDecoration: "none", marginBottom: 36 }}>
          <img src="/theme/images/favicon.png" alt="Treasury star" style={{ height: 56, width: 56, borderRadius: 12, display: "block" }} />
          <span style={{ fontFamily: "'Ibarra Real Nova', Georgia, serif", fontSize: 36, fontWeight: 700, color: "#ffffff", letterSpacing: "0.04em", lineHeight: 1 }}>TREASURY</span>
        </Link>
        <h1 style={{ color: "#fff", fontSize: 32, fontWeight: 800, textAlign: "center", margin: "0 0 16px", lineHeight: 1.2 }}>
          {t("auth.registerHeroTitle")}
        </h1>
        <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 15, textAlign: "center", maxWidth: 340, lineHeight: 1.7, margin: "0 0 40px" }}>
          {t("auth.registerHeroSubtitle")}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 340 }}>
          {[
            {
              svg: <svg width="20" height="20" fill="none" stroke="#c9a84c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
              title: t("auth.featurePremium"),
              desc: t("auth.featurePremiumDesc")
            },
            {
              svg: <svg width="20" height="20" fill="none" stroke="#c9a84c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
              title: t("auth.featureBlockchain"),
              desc: t("auth.featureBlockchainDesc")
            },
            {
              svg: <svg width="20" height="20" fill="none" stroke="#c9a84c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
              title: t("auth.featureReturns"),
              desc: t("auth.featureReturnsDesc")
            },
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(201,168,76,0.20)", borderRadius: 12, padding: "16px 20px", backdropFilter: "blur(8px)" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {f.svg}
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 13.5 }}>{f.title}</div>
                <div style={{ color: "rgba(255,255,255,0.60)", fontSize: 12.5, marginTop: 2 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel — Form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px", overflowY: "auto" }}>
        <div style={{ width: "100%", maxWidth: 480 }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: "#111827", margin: "0 0 8px" }}>{t("auth.createAccount")}</h2>
            <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
              {t("auth.alreadyHaveAccount")}{" "}
              <Link to="/login" style={{ color: "#c9a84c", fontWeight: 600, textDecoration: "none" }}>{t("auth.signIn")}</Link>
            </p>
          </div>

          <Stepper step={kycData ? 2 : 1} isRtl={isRtl} />

          {!kycData && auth.data?.errors?.kyc && (
            <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "#dc2626" }}>
              {isRtl ? "انتهت صلاحية التحقق عبر نفاذ. يرجى التحقق مرة أخرى لإكمال التسجيل." : "Your Nafath verification expired. Please verify again to finish registering."}
            </div>
          )}

          {!kycData ? (
            <NafathKycStep
              onVerified={setKycData}
              initialKycStatus={initialKycStatus}
              initialSessionToken={initialSessionToken}
              initialReason={initialReason}
            />
          ) : (
          <div>
            <VerifiedIdentityCard identity={kycData.identity || {}} isRtl={isRtl} />

            {auth.data?.status === 0 && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#dc2626" }}>
                {auth.data?.errors?.message || auth.data?.errors?.kyc || t("auth.registrationFailed")}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Full Name — pre-filled from Nafath if available */}
              <div>
                <label style={labelStyle}>{t("auth.fullName")}</label>
                <input name="name" type="text" placeholder={t("auth.fullNamePlaceholder")}
                  defaultValue={verifiedName}
                  key={verifiedName || "name-empty"}
                  style={{ ...inputStyle, borderColor: focusedField === "name" ? "#c9a84c" : errors.name ? "#ef4444" : "#e5e7eb" }}
                  onChange={handleChange} onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField("")} />
                <span style={errorStyle}>{errors.name || serverErrors.name}</span>
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>{t("auth.email")}</label>
                <input name="email" type="email" placeholder={t("auth.emailPlaceholder")} style={{ ...inputStyle, borderColor: focusedField === "email" ? "#c9a84c" : errors.email ? "#ef4444" : "#e5e7eb" }}
                  onChange={handleChange} onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField("")} />
                <span style={errorStyle}>{errors.email || serverErrors.email}</span>
              </div>

              {/* National ID */}
              <div>
                <label style={labelStyle}>{t("auth.nationalId")}</label>
                <input name="national_id" type="text" placeholder="XXXXXXXXXX"
                  defaultValue={kycData?.identity?.nationalId || ""}
                  key={kycData?.identity?.nationalId || "nid-empty"}
                  readOnly={!!kycData?.identity?.nationalId}
                  style={{ ...inputStyle, borderColor: kycData ? "#86efac" : focusedField === "national_id" ? "#c9a84c" : errors.national_id ? "#ef4444" : "#e5e7eb", background: kycData?.identity?.nationalId ? "#f0fdf4" : "#f9fafb" }}
                  onChange={handleChange} onFocus={() => setFocusedField("national_id")} onBlur={() => setFocusedField("")} />
                <span style={errorStyle}>{errors.national_id || serverErrors.national_id}</span>
              </div>

              {/* Phone */}
              <div>
                <label style={labelStyle}>{t("auth.phoneNumber")}</label>
                <div style={{ display: "flex", gap: 8, direction: "ltr" }}>
                  <div dir="ltr" style={{ ...inputStyle, width: 76, flexShrink: 0, background: "#f3f4f6", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 13, cursor: "default", direction: "ltr", unicodeBidi: "isolate" }}>
                    +966
                  </div>
                  <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                    <span dir="ltr" style={{ position: "absolute", left: 14, color: "#374151", fontWeight: 600, fontSize: 14, pointerEvents: "none", userSelect: "none", direction: "ltr", unicodeBidi: "isolate" }}>5</span>
                    <input
                      name="phone_no"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      placeholder="XXXXXXXX"
                      dir="ltr"
                      style={{ ...inputStyle, direction: "ltr", textAlign: "left", paddingLeft: 30, borderColor: focusedField === "phone_no" ? "#c9a84c" : errors.phone_no ? "#ef4444" : "#e5e7eb" }}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        e.target.value = digits;
                        handleChange(e);
                      }}
                      onFocus={() => setFocusedField("phone_no")}
                      onBlur={() => setFocusedField("")}
                    />
                  </div>
                </div>
                <span style={errorStyle}>{errors.phone_no || serverErrors.phone_no}</span>
              </div>

              {/* Password */}
              <div>
                <label style={labelStyle}>{t("auth.password")}</label>
                <div style={{ position: "relative" }}>
                  <input name="password" type={showPass ? "text" : "password"} placeholder={t("auth.passwordPlaceholder")}
                    style={{ ...inputStyle, paddingRight: 44, borderColor: focusedField === "password" ? "#c9a84c" : errors.password ? "#ef4444" : "#e5e7eb" }}
                    autoComplete="new-password"
                    onChange={handleChange} onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField("")} />
                  <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0 }}>
                    {showPass ? (
                      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
                <span style={errorStyle}>{errors.password || serverErrors.password}</span>
              </div>

              {/* Confirm Password */}
              <div>
                <label style={labelStyle}>{t("auth.confirmPassword")}</label>
                <input name="repeat_password" type="password" placeholder={t("auth.confirmPasswordPlaceholder")}
                  style={{ ...inputStyle, borderColor: focusedField === "repeat_password" ? "#c9a84c" : errors.repeat_password ? "#ef4444" : "#e5e7eb" }}
                  autoComplete="new-password"
                  onChange={handleChange} onFocus={() => setFocusedField("repeat_password")} onBlur={() => setFocusedField("")} />
                <span style={errorStyle}>{errors.repeat_password || serverErrors.repeat_password}</span>
              </div>

              {/* Terms */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                <input type="checkbox" checked={checkBox} onChange={(e) => setCheckBox(e.target.checked)}
                  style={{ marginTop: 2, accentColor: "#c9a84c", width: 16, height: 16, flexShrink: 0 }} />
                {t("auth.agreeTerms")}{" "}
                <Link to="/terms" style={{ color: "#c9a84c", textDecoration: "none", fontWeight: 600 }}>{t("auth.termsOfService")}</Link>
                {" "}{t("auth.and")}{" "}
                <Link to="/privacy" style={{ color: "#c9a84c", textDecoration: "none", fontWeight: 600 }}>{t("auth.privacyPolicy")}</Link>
              </label>

              {/* Submit */}
              <button type="submit" disabled={!checkBox}
                style={{ width: "100%", padding: "14px", background: checkBox ? "linear-gradient(135deg, #c9a84c, #a07a20)" : "#e5e7eb", color: checkBox ? "#fff" : "#9ca3af", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: checkBox ? "pointer" : "not-allowed", transition: "all 0.2s", marginTop: 4 }}>
                {t("auth.createAccount")}
              </button>
            </form>
          </div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .register-left-panel { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Register;
